import { CartAddEvent, CartGroupedSections, ThemeEvents } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

/**
 * Build Your Set
 * -----------------------------------------------------------------------------
 * Lets a customer pick individual products (e.g. fragrance bottles) up to a
 * target quantity (1 / 2 / 4 ...). Each pick is a REAL product, so Shopify
 * manages inventory automatically. The tiered price is achieved with a native
 * Shopify Automatic Discount that the merchant configures to match the tiers
 * defined in the theme editor — this script only *previews* the saving.
 *
 * Custom element: <build-your-set>
 */
class BuildYourSet extends HTMLElement {
  constructor() {
    super();
    /** @type {Map<string, {qty:number, price:number, title:string}>} */
    this.selected = new Map();
    this.target = 0;
    this.tiers = [];
    this.minItems = 1;
    this.maxItems = 0; // 0 = no hard cap
    this.allowDuplicates = false;
    this.moneyFormat = window.FoxTheme?.moneyFormat || window.Shopify?.currency?.money_format || "${{amount}}";
  }

  connectedCallback() {
    // Read config
    this.minItems = parseInt(this.dataset.minItems || "1", 10);
    this.maxItems = parseInt(this.dataset.maxItems || "0", 10);
    this.allowDuplicates = this.dataset.allowDuplicates === "true";

    const tiersEl = this.querySelector('[data-bys="tiers"]');
    if (tiersEl) {
      try {
        this.tiers = JSON.parse(tiersEl.textContent).sort((a, b) => a.qty - b.qty);
      } catch (e) {
        this.tiers = [];
      }
    }

    // Default target = largest tier so the progress bar has a goal
    this.target = this.tiers.length ? this.tiers[this.tiers.length - 1].qty : this.maxItems || 0;

    this.grid = this.querySelector('[data-bys="grid"]');
    this.summaryCount = this.querySelector('[data-bys="count"]');
    this.summaryTarget = this.querySelector('[data-bys="target"]');
    this.summarySubtotal = this.querySelector('[data-bys="subtotal"]');
    this.summarySaving = this.querySelector('[data-bys="saving"]');
    this.summaryTotal = this.querySelector('[data-bys="total"]');
    this.progressBar = this.querySelector('[data-bys="progress-bar"]');
    this.addButton = this.querySelector('[data-bys="add"]');
    this.tierButtons = [...this.querySelectorAll('[data-bys="tier"]')];
    this.message = this.querySelector('[data-bys="message"]');

    // Events
    this.addEventListener("click", this.#onClick);
    this.addButton?.addEventListener("click", this.#onAddToCart);

    this.#syncTierButtons();
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.#onClick);
  }

  #onClick = (event) => {
    const tierBtn = event.target.closest('[data-bys="tier"]');
    if (tierBtn) {
      this.target = parseInt(tierBtn.dataset.qty, 10);
      this.#syncTierButtons();
      this.render();
      return;
    }

    const addBtn = event.target.closest('[data-bys="pick"]');
    if (addBtn) {
      this.#addOne(addBtn.closest("[data-variant-id]"));
      return;
    }

    const incBtn = event.target.closest('[data-bys="inc"]');
    if (incBtn) {
      this.#changeQty(incBtn.closest("[data-variant-id]"), 1);
      return;
    }

    const decBtn = event.target.closest('[data-bys="dec"]');
    if (decBtn) {
      this.#changeQty(decBtn.closest("[data-variant-id]"), -1);
      return;
    }
  };

  #totalCount() {
    let n = 0;
    this.selected.forEach((v) => (n += v.qty));
    return n;
  }

  #addOne(card) {
    if (!card) return;
    if (this.maxItems && this.#totalCount() >= this.maxItems) {
      this.#flash(card);
      return;
    }
    const id = card.dataset.variantId;
    const existing = this.selected.get(id);
    if (existing) {
      if (this.allowDuplicates) existing.qty += 1;
    } else {
      this.selected.set(id, {
        qty: 1,
        price: parseInt(card.dataset.price, 10),
        title: card.dataset.title || "",
      });
    }
    this.render();
  }

  #changeQty(card, delta) {
    if (!card) return;
    const id = card.dataset.variantId;
    const existing = this.selected.get(id);
    if (!existing) {
      if (delta > 0) this.#addOne(card);
      return;
    }
    if (delta > 0 && this.maxItems && this.#totalCount() >= this.maxItems) return;
    existing.qty += delta;
    if (existing.qty <= 0) this.selected.delete(id);
    this.render();
  }

  #currentTier(count) {
    let tier = null;
    for (const t of this.tiers) {
      if (count >= t.qty) tier = t;
    }
    return tier;
  }

  #syncTierButtons() {
    this.tierButtons.forEach((btn) => {
      const active = parseInt(btn.dataset.qty, 10) === this.target;
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
    });
  }

  render() {
    const count = this.#totalCount();
    let subtotal = 0;
    this.selected.forEach((v) => (subtotal += v.price * v.qty));

    const tier = this.#currentTier(count);
    const discountPct = tier ? Number(tier.discount_percent || 0) : 0;
    const saving = Math.round((subtotal * discountPct) / 100);
    const total = subtotal - saving;

    // Update card visual state + qty badges
    this.querySelectorAll("[data-variant-id]").forEach((card) => {
      const entry = this.selected.get(card.dataset.variantId);
      const qty = entry ? entry.qty : 0;
      card.classList.toggle("is-selected", qty > 0);
      const badge = card.querySelector('[data-bys="qty"]');
      if (badge) badge.textContent = qty;
      const qtyNum = card.querySelector('[data-bys="qty-num"]');
      if (qtyNum) qtyNum.textContent = qty;
      const controls = card.querySelector('[data-bys="controls"]');
      const pick = card.querySelector('[data-bys="pick"]');
      if (controls && pick) {
        controls.hidden = qty === 0;
        pick.hidden = qty > 0;
      }
    });

    if (this.summaryCount) this.summaryCount.textContent = count;
    if (this.summaryTarget) this.summaryTarget.textContent = this.target || "—";
    if (this.summarySubtotal) this.summarySubtotal.textContent = this.#money(subtotal);
    if (this.summarySaving) this.summarySaving.textContent = "-" + this.#money(saving);
    if (this.summaryTotal) this.summaryTotal.textContent = this.#money(total);

    if (this.progressBar) {
      const goal = this.target || this.maxItems || count || 1;
      this.progressBar.style.width = Math.min(100, (count / goal) * 100) + "%";
    }

    // Saving row visibility
    const savingRow = this.querySelector('[data-bys="saving-row"]');
    if (savingRow) savingRow.hidden = saving <= 0;

    // Add button state
    if (this.addButton) {
      const ok = count >= this.minItems;
      this.addButton.disabled = !ok;
      this.addButton.classList.toggle("btn--disabled", !ok);
    }

    if (this.message) {
      if (count < this.minItems) {
        this.message.textContent = (this.dataset.msgMin || "Select at least {min} items").replace(
          "{min}",
          this.minItems
        );
        this.message.hidden = false;
      } else if (tier) {
        this.message.textContent = (this.dataset.msgSaving || "You save {amount}!").replace(
          "{amount}",
          this.#money(saving)
        );
        this.message.hidden = false;
      } else {
        this.message.hidden = true;
      }
    }
  }

  #flash(card) {
    card.classList.add("bys-shake");
    setTimeout(() => card.classList.remove("bys-shake"), 400);
  }

  #gatherSectionIds() {
    const sections = [];
    document.dispatchEvent(new CartGroupedSections(sections));
    return [...new Set(sections)];
  }

  #onAddToCart = async () => {
    const count = this.#totalCount();
    if (count < this.minItems) return;

    const items = [];
    this.selected.forEach((v, id) => items.push({ id: Number(id), quantity: v.qty }));
    if (!items.length) return;

    this.addButton.classList.add("btn--loading");
    this.addButton.disabled = true;

    const sectionIds = this.#gatherSectionIds();
    const body = JSON.stringify({ items, sections: sectionIds.join(",") });

    try {
      const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body }));
      const parsed = await res.json();

      if (parsed.status) {
        this.#showError(parsed.description || parsed.message);
        return;
      }

      // Reset selection then open the cart drawer via the theme's own event
      this.selected.clear();
      this.render();

      this.dispatchEvent(
        new CartAddEvent(parsed, this.id || "build-your-set", {
          source: "build-your-set",
          itemCount: count,
          sections: parsed.sections,
        })
      );
      document.dispatchEvent(new CustomEvent(ThemeEvents.productAjaxAdded, { detail: { product: parsed } }));
    } catch (e) {
      this.#showError(e.message);
    } finally {
      this.addButton.classList.remove("btn--loading");
      this.render();
    }
  };

  #showError(text) {
    if (this.message) {
      this.message.textContent = text;
      this.message.hidden = false;
    }
  }

  #money(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(cents, this.moneyFormat);
    }
    // Minimal fallback
    const amount = (cents / 100).toFixed(2);
    return this.moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }
}

if (!customElements.get("build-your-set")) {
  customElements.define("build-your-set", BuildYourSet);
}
